import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  createPersonAvatarProps,
  Surface,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import {
  createColumnHelper,
  flexRender,
  HeaderGroup,
  Row,
  SortingState,
  TableState,
} from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import { CustomColumnDef } from '@experiences/components/table/types';
import {
  ESTIMATED_HEADER_HEIGHT,
  MIN_NAME_COLUMN_SIZE,
  NAME_COLUMN_PERCENTAGE,
  ROW_HEIGHT,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-table-constants';
import { LayoutChangeEvent, View } from 'react-native';
import {
  ManagerRepColumnId,
  ManagerRepRowData,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';
import { ManagerSectionError } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-section-error';
import { ManagerTableRow } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-table-row';
import { ManagerTableSkeleton } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-table-skeleton';
import { Table } from '@experiences/components/table/table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';

const REP_METRIC_COLUMN_COUNT = 4;

const REP_METRIC_COLUMN_PERCENTAGE =
  (100 - NAME_COLUMN_PERCENTAGE) / REP_METRIC_COLUMN_COUNT;

const MIN_METRIC_COLUMN_SIZE = 72;

const CARD_HEAD_MIN_HEIGHT = 48;

const DEFAULT_REP_SORTING: SortingState = [
  { desc: true, id: ManagerRepColumnId.Value },
];

const PINNED_COLUMNS = { left: [ManagerRepColumnId.Rep] };

const columnHelper = createColumnHelper<ManagerRepRowData>();

interface ProspectsManagerRepsTableProps {
  data: ManagerRepRowData[];
  emptyComponent?: React.ReactNode;
  hasError: boolean;
  isLoading: boolean;
  onRetry: () => void;
  toolbar?: React.ReactNode;
}

export const ProspectsManagerRepsTable: React.FC<
  ProspectsManagerRepsTableProps
> = ({ data, emptyComponent, hasError, isLoading, onRetry, toolbar }) => {
  const [styles, theme] = useStyles();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_REP_SORTING);

  const tableState = useMemo(
    (): Partial<TableState> => ({ sorting }),
    [sorting],
  );

  const handleLayout = (event: LayoutChangeEvent) => {
    setLayoutWidth(event.nativeEvent.layout.width);
  };

  const renderHeaderLabel = useCallback(
    (translationKey: string) => (
      <Typography
        color={theme.sk.color.text.secondary}
        size="$xs"
        variant="label"
      >
        {i18n.t(translationKey)}
      </Typography>
    ),
    [theme],
  );

  const renderBodyText = useCallback(
    (value: string) => (
      <Typography numberOfLines={1} size="$sm" textAlign="left" variant="body">
        {value}
      </Typography>
    ),
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.repName, {
        cell: (info) => {
          const row = info.row.original;

          return (
            <View style={styles.repCellContainer}>
              <Avatar
                {...createPersonAvatarProps(row.repName, row.repUUID)}
                size="$sm"
              />
              <Typography
                fontWeight="$medium"
                numberOfLines={1}
                size="$sm"
                textAlign="left"
              >
                {row.repName}
              </Typography>
            </View>
          );
        },
        header: () =>
          renderHeaderLabel('sales_portal.prospects.manager.table.rep'),
        id: ManagerRepColumnId.Rep,
        meta: { initialPercentWidth: NAME_COLUMN_PERCENTAGE },
        minSize: MIN_NAME_COLUMN_SIZE,
        sortDescFirst: false,
        sortingFn: 'text',
      }),
      columnHelper.accessor((row) => row.weeklyValueInMicros, {
        cell: (info) => renderBodyText(info.row.original.weeklyValueDisplay),
        header: () =>
          renderHeaderLabel('sales_portal.prospects.manager.table.rep_value'),
        id: ManagerRepColumnId.Value,
        meta: { initialPercentWidth: REP_METRIC_COLUMN_PERCENTAGE },
        minSize: MIN_METRIC_COLUMN_SIZE,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => row.conversionRate, {
        cell: (info) => renderBodyText(info.row.original.conversionRateDisplay),
        header: () =>
          renderHeaderLabel('sales_portal.prospects.manager.table.conversion'),
        id: ManagerRepColumnId.Conversion,
        meta: { initialPercentWidth: REP_METRIC_COLUMN_PERCENTAGE },
        minSize: MIN_METRIC_COLUMN_SIZE,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => row.avgConversionDays, {
        cell: (info) =>
          renderBodyText(info.row.original.avgConversionDaysDisplay),
        header: () =>
          renderHeaderLabel('sales_portal.prospects.manager.table.avg_time'),
        id: ManagerRepColumnId.AvgTime,
        meta: { initialPercentWidth: REP_METRIC_COLUMN_PERCENTAGE },
        minSize: MIN_METRIC_COLUMN_SIZE,
        sortDescFirst: false,
        sortUndefined: 'last',
      }),
      columnHelper.accessor((row) => row.noteCount, {
        cell: (info) => renderBodyText(String(info.row.original.noteCount)),
        header: () =>
          renderHeaderLabel('sales_portal.prospects.manager.table.notes'),
        id: ManagerRepColumnId.Notes,
        meta: { initialPercentWidth: REP_METRIC_COLUMN_PERCENTAGE },
        minSize: MIN_METRIC_COLUMN_SIZE,
      }),
    ],
    [renderBodyText, renderHeaderLabel, styles],
  );

  const renderHeaderRow = ({
    headerGroup,
  }: {
    headerGroup: HeaderGroup<ManagerRepRowData>;
  }) => (
    <TableHeaderRow key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <TableHeaderCell
          columnId={header.id}
          key={header.id}
          onPress={
            header.column.getCanSort()
              ? () => header.column.toggleSorting()
              : undefined
          }
          pinnedOffset={
            header.column.getIsPinned()
              ? header.column.getStart('left')
              : undefined
          }
          sortDirection={header.column.getIsSorted() || undefined}
        >
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </TableHeaderCell>
      ))}
    </TableHeaderRow>
  );

  const renderRow = useCallback(
    ({ index, row }: { index: number; row: Row<ManagerRepRowData> }) => (
      <ManagerTableRow index={index} key={row.id} row={row} />
    ),
    [],
  );

  const renderCardBody = () => {
    if (isLoading && data.length === 0) {
      return (
        <ManagerTableSkeleton
          columnCount={REP_METRIC_COLUMN_COUNT}
          columnPercentage={REP_METRIC_COLUMN_PERCENTAGE}
          minColumnWidth={MIN_METRIC_COLUMN_SIZE}
        />
      );
    }

    if (hasError) {
      return <ManagerSectionError onRetry={onRetry} />;
    }

    if (!layoutWidth) {
      return null;
    }

    return (
      <Table
        cellPadding={theme.sk.spacing.spaceMedium}
        columns={columns as CustomColumnDef<ManagerRepRowData>[]}
        data={data}
        emptyComponent={emptyComponent}
        estimatedHeaderHeight={ESTIMATED_HEADER_HEIGHT}
        estimatedRowHeight={ROW_HEIGHT}
        gutters={0}
        headerPadding={theme.sk.spacing.spaceMedium}
        initialState={{ columnPinning: PINNED_COLUMNS }}
        isActive
        layoutWidth={layoutWidth}
        onSortingChange={setSorting}
        renderHeaderRow={renderHeaderRow}
        renderRow={renderRow}
        state={tableState}
      />
    );
  };

  return (
    <Surface flex={1}>
      <YStack flex={1} gap="$12" minHeight={0}>
        <XStack
          alignItems="center"
          gap="$8"
          minHeight={CARD_HEAD_MIN_HEIGHT}
          paddingHorizontal="$16"
          paddingTop="$8"
        >
          <Typography
            color="$textPrimary"
            flex={1}
            size="$md"
            variant="heading"
          >
            {i18n.t('sales_portal.prospects.manager.reps_heading')}
          </Typography>
          {toolbar}
        </XStack>

        <View onLayout={handleLayout} style={styles.tableWrapper}>
          {renderCardBody()}
        </View>
      </YStack>
    </Surface>
  );
};

const useStyles = createStyles((theme) => ({
  repCellContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
  },
  tableWrapper: {
    flex: 1,
  },
}));
